import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiArrowLeft, FiUpload, FiFileText, FiMonitor,
  FiUser, FiLogOut, FiChevronDown, FiMail, FiHash,
} from 'react-icons/fi';
import OCRProcess       from './OCRProcess';
import OCRDocumentAudit from './OCRDocumentAudit';
import OCRVendorMonitor from './OCRVendorMonitor';
import OCRChatWidget    from './OCRChatWidget';
import '../../Styles/OCR.css';

// ── Vendor avatar initials ────────────────────────────────────────────────────
function getInitials(name) {
  if (!name) return 'V';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

// ── Vendor profile dropdown ───────────────────────────────────────────────────
const VendorProfileMenu = ({ vendorName, userEmail, vendorCode, onLogout }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initials = getInitials(vendorName);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '6px 12px 6px 6px',
          background: 'linear-gradient(135deg, rgba(239,246,255,.9), rgba(255,244,234,.7))',
          border: '1.5px solid rgba(216,227,240,.9)',
          borderRadius: 999, cursor: 'pointer',
          transition: 'all .18s', backdropFilter: 'blur(8px)',
        }}
      >
        {/* Avatar */}
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: 'linear-gradient(135deg, #0f4c81, #147699)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontSize: '0.75rem', fontWeight: 800, flexShrink: 0,
          boxShadow: '0 2px 8px rgba(15,76,129,.3)',
        }}>
          {initials}
        </div>
        <div style={{ textAlign: 'left', minWidth: 0 }}>
          <div style={{
            fontSize: '0.82rem', fontWeight: 800, color: '#0f1f35',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: 160,
          }}>
            {vendorName || 'Vendor Account'}
          </div>
          {vendorCode && (
            <div style={{ fontSize: '0.68rem', color: '#7a96b2', fontWeight: 600 }}>
              Code: {vendorCode}
            </div>
          )}
        </div>
        <FiChevronDown
          size={13}
          style={{
            color: '#4a6080', flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .18s',
          }}
        />
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              minWidth: 260, background: 'rgba(255,255,255,0.96)',
              border: '1px solid rgba(216,227,240,.8)',
              borderRadius: 16, boxShadow: '0 12px 40px rgba(12,28,52,0.16)',
              backdropFilter: 'blur(14px)', zIndex: 100, overflow: 'hidden',
            }}
          >
            {/* Profile header */}
            <div style={{
              padding: '16px 18px',
              background: 'linear-gradient(135deg, #0f4c81, #147699)',
              color: '#fff',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: 'rgba(255,255,255,.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '1rem', fontWeight: 800, flexShrink: 0,
                }}>
                  {initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem', fontFamily: "'Space Grotesk', sans-serif" }}>
                    {vendorName || 'Vendor Account'}
                  </div>
                  <div style={{ fontSize: '0.72rem', opacity: 0.8, marginTop: 2 }}>Vendor Portal</div>
                </div>
              </div>
            </div>

            {/* Info rows */}
            <div style={{ padding: '10px 0' }}>
              {userEmail && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 18px', fontSize: '0.82rem', color: '#4a6080' }}>
                  <FiMail size={13} style={{ color: '#0f4c81', flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userEmail}</span>
                </div>
              )}
              {vendorCode && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 18px', fontSize: '0.82rem', color: '#4a6080' }}>
                  <FiHash size={13} style={{ color: '#0f4c81', flexShrink: 0 }} />
                  <span>Vendor Code: <strong style={{ color: '#12213a' }}>{vendorCode}</strong></span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: 'rgba(216,227,240,.7)', margin: '0 14px' }} />

            {/* Logout */}
            <div style={{ padding: '8px 0 6px' }}>
              <button
                onClick={onLogout}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 18px', border: 'none', background: 'none',
                  cursor: 'pointer', fontSize: '0.84rem', fontWeight: 700,
                  color: '#a42f20', transition: 'background .15s',
                  fontFamily: "'Manrope', sans-serif",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#ffebee'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
              >
                <FiLogOut size={14} />
                Sign Out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── Main App ──────────────────────────────────────────────────────────────────
const OCRApp = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('process');
  const [uploadedInvoices, setUploadedInvoices] = useState([]);

  // Read vendor info from localStorage
  const vendorName  = localStorage.getItem('vendorName')  || '';
  const userEmail   = localStorage.getItem('userEmail')   || '';
  const vendorCode  = localStorage.getItem('vendorCode')  || '';

  const handleUploadComplete = (invoiceNumbers) => setUploadedInvoices(invoiceNumbers);

  const handleLogout = () => {
    ['isAuthenticated', 'userType', 'userEmail', 'vendorName', 'vendorCode', 'session_id', 'token'].forEach(
      (k) => localStorage.removeItem(k)
    );
    navigate('/vendor-login', { replace: true });
  };

  const TABS = [
    { id: 'process', label: 'Invoice Processing', icon: <FiUpload size={14} /> },
    {
      id: 'audit',
      label: 'Document & Audit',
      icon: <FiFileText size={14} />,
      badge: uploadedInvoices.length > 0 ? uploadedInvoices.length : null,
    },
    { id: 'vendor', label: 'Vendor Monitor', icon: <FiMonitor size={14} /> },
  ];

  return (
    <motion.div
      className="inv-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* ── Header ── */}
      <motion.div
        className="inv-header"
        initial={{ y: -48, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Left: back + title */}
        <div className="inv-header-left">
          <motion.button
            className="inv-back-btn"
            onClick={() => navigate('/home')}
            whileHover={{ x: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <FiArrowLeft size={13} />
            <span>Home</span>
          </motion.button>

          <motion.div
            className="inv-header-title"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1, duration: 0.38 }}
          >
            <h1>Invoice Intelligence Hub</h1>
            <p>OCR Extraction · Review · Vendor Monitor · AI Assistant</p>
          </motion.div>
        </div>

        {/* Right: status badge + vendor profile */}
        <motion.div
          style={{ display: 'flex', alignItems: 'center', gap: 12 }}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.18, duration: 0.38 }}
        >
          {/* Live API badge */}
          <div className="inv-api-badge">
            <div className="inv-api-dot" />
            <span>ocrtool.savictech.com</span>
          </div>

          {/* Vendor profile chip */}
          <VendorProfileMenu
            vendorName={vendorName}
            userEmail={userEmail}
            vendorCode={vendorCode}
            onLogout={handleLogout}
          />
        </motion.div>
      </motion.div>

      {/* ── Welcome strip (shown when vendor name is known) ── */}
      {vendorName && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.32, duration: 0.35 }}
          style={{
            display: 'flex', alignItems: 'center', gap: 14,
            padding: '10px 24px',
            background: 'linear-gradient(135deg, rgba(15,76,129,.08) 0%, rgba(229,110,17,.06) 100%)',
            borderBottom: '1px solid rgba(216,227,240,.6)',
            fontSize: '0.82rem', color: '#4a6080',
          }}
        >
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'linear-gradient(135deg,#0f4c81,#147699)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: '0.65rem', fontWeight: 800, flexShrink: 0,
          }}>
            {getInitials(vendorName)}
          </div>
          <span>
            Welcome back, <strong style={{ color: '#0f1f35', fontWeight: 800 }}>{vendorName}</strong>
            {vendorCode && <> &nbsp;·&nbsp; Code: <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f4c81' }}>{vendorCode}</span></>}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiMail size={12} style={{ color: '#7a96b2' }} />
            <span style={{ fontSize: '0.76rem', color: '#7a96b2' }}>{userEmail}</span>
          </div>
        </motion.div>
      )}

      {/* ── Tab bar ── */}
      <div className="inv-tab-bar">
        {TABS.map((tab, i) => (
          <motion.button
            key={tab.id}
            className={`inv-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.06, duration: 0.28 }}
            whileTap={{ scale: 0.97 }}
            style={{ position: 'relative' }}
          >
            {tab.icon}
            {tab.label}
            {tab.badge && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                style={{
                  position: 'absolute', top: 5, right: 5,
                  background: 'linear-gradient(135deg,#e56e11,#fb923c)',
                  color: '#fff', fontSize: '0.58rem', fontWeight: 800,
                  width: 17, height: 17, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(229,110,17,.4)',
                }}
              >
                {tab.badge}
              </motion.span>
            )}
          </motion.button>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="inv-content">
        {/* All tabs rendered but hidden — preserves state on switch */}
        <div style={{ display: activeTab === 'process' ? 'block' : 'none' }}>
          <OCRProcess onUploadComplete={handleUploadComplete} />
        </div>
        <div style={{ display: activeTab === 'audit' ? 'block' : 'none' }}>
          <OCRDocumentAudit uploadedInvoices={uploadedInvoices} />
        </div>
        <div style={{ display: activeTab === 'vendor' ? 'block' : 'none' }}>
          <OCRVendorMonitor />
        </div>
      </div>

      {/* ── Floating Chat Widget ── */}
      <OCRChatWidget />
    </motion.div>
  );
};

export default OCRApp;
